import { createContext, useContext } from "react";

/*
 * Locale identifier is a triple "language[-script][-region]".
 *
 * Examples are:
 *
 * - "en" -- English.
 * - "en-US" -- English, United States.
 * - "en-CA" -- English, Canada.
 * - "zh-Hans" Chinese, Simplified Script.
 * - "zh-Hant" Chinese, Traditional Script.
 * - "zh-CN" Chinese, China.
 * - "zh-TW" Chinese, Taiwan.
 * - "zh-Hans-CN" Chinese, Simplified Script, China.
 * - "zh-Hant-TW" Chinese, Traditional Script, Taiwan.
 *
 * @see https://www.w3.org/International/articles/bcp47/
 * @see https://www.rfc-editor.org/rfc/rfc5646.txt
 */

export type LocaleId = string;

export const defaultLocale: LocaleId = "en";

export const allLocales: readonly LocaleId[] = [
  defaultLocale,
  "af",
  "ar",
  "bg",
  "ca",
  "cs",
  "da",
  "de",
  "el",
  "eo",
  "es",
  "et",
  "fa",
  "fi",
  "fr",
  "ga",
  "he",
  "hr",
  "hu",
  "id",
  "it",
  "ja",
  "ko",
  "ne",
  "nl",
  "pl",
  "pt-br",
  "pt-pt",
  "ro",
  "ru",
  "sk",
  "sv",
  "th",
  "tr",
  "uk",
  "vi",
  "zh-hans",
  "zh-hant",
  "zh-tw",
];

/**
 * Locales whose landing-page copy is actually translated.
 *
 * The application UI is translated into every locale in `allLocales`, but the
 * landing page is marketing copy that has to be written rather than merely
 * mapped, and it is only worth writing where there are readers. Locales absent
 * from this list are still fully usable - `/ne/practice` is Nepali - they just
 * do not get their own indexable copy of the landing page: it would be the
 * English one at a different address, which is how 39 identical front pages
 * ended up competing with each other in search results and none of them being
 * indexed.
 *
 * Consequences of adding a locale here, all automatic: the landing page stops
 * self-canonicalising to English, it appears in the sitemap, and it is listed
 * as an hreflang alternate. So add a locale only once every landing message in
 * `translations/<locale>.json` is genuinely translated - listing one early
 * re-creates the exact duplicate-content problem this list exists to prevent.
 */
export const landingLocales: readonly LocaleId[] = [
  defaultLocale,
  "de",
  "es",
  "fr",
  "it",
  "ja",
  "pl",
  "pt-br",
  "ru",
  "tr",
  "zh-hans",
];

export function hasLandingTranslation(locale: LocaleId): boolean {
  return landingLocales.includes(locale);
}

export function getDir(locale: LocaleId): "ltr" | "rtl" {
  switch (locale) {
    case "ar":
    case "fa":
    case "he":
      return "rtl";
    default:
      return "ltr";
  }
}

export const PreferredLocaleContext = createContext<LocaleId>(defaultLocale);

export function usePreferredLocale(): LocaleId {
  return useContext(PreferredLocaleContext);
}

const map = (() => {
  const tmp = new Map<string, LocaleId>();
  for (const id of allLocales) {
    const { language, region } = new Intl.Locale(id).maximize();
    tmp.set(language + "-" + region, id);
    if (!tmp.has(language)) {
      tmp.set(language, id);
    }
  }
  return tmp;
})();

export function selectLocale(
  filter: (...locales: readonly string[]) => string | null,
): LocaleId {
  return map.get(filter(...map.keys()) ?? "") ?? defaultLocale;
}
